import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@/lib/components/Button';
import FormField from '@/lib/components/FormField';
import { useFormSave } from '@/lib/hooks/useFormSave';

import styles from '@/lib/components/PspComposer/PspComposer.module.scss';
import internationalTransfersUsdStyles from './InternationalTransfersUsd.module.scss';

const COUNTRY_UNITED_STATES = 'United States of America';
const ACCOUNT_TYPE_OPTIONS = [
    { value: '', label: '' },
    { value: 'Checking account', label: 'Checking account' },
    { value: 'Savings account', label: 'Savings account' },
];

/**
 * This component is one of the multiple available payment method in this project.
 * It represents a US international bank transfer form, only used to collect refund request data for now.
 */
const InternationalTransfersUsd = React.forwardRef(
    (
        {
            pspId,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,
            onPaymentResult,
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const formRef = React.useRef(null);
        const [formData, setFormData] = React.useState({
            'first_name': '',
            'last_name': '',
            'routing_number': '',
            'account_number': '',
            'bank_account_type': '',
            'country': COUNTRY_UNITED_STATES,
        });

        const [errors, setErrors] = React.useState({});

        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response });
            },
            [onPaymentResult],
        );

        const handleError = React.useCallback(
            (response) => {
                if (response?.data) {
                    const fieldErrors = {};
                    Object
                        .keys(response.data)
                        .forEach((key) => {
                            fieldErrors[key] = response.data[key] && response.data[key].map((error) => t([
                                `error_${error.code}`,
                                'error_form',
                            ]));
                        });
                    setErrors(fieldErrors);
                }
                onPaymentResult({
                    success: false,
                    data: response,
                });
            },
            [onPaymentResult, t],
        );

        const url = lyriaUrl + '/international-transfers-usd/save';
        const saveForm = useFormSave(url, token, paymentRequestId, pspId, handleSuccess, handleError);

        const submitForm = React.useCallback(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                saveForm(formData);
            },
            [formData, saveForm],
        );

        const onChangeFormField = React.useCallback(
            (fieldName) => (event, newValue) => {
                setFormData((prevState) => ({
                    ...prevState,
                    [fieldName]: newValue,
                }));

            },
            [],
        );

        const formatAndValidateRoutingNumber = React.useCallback(
            (value) => {
                const digitsOnly = value.replace(/\D/g, '').slice(0, 9);
                let routingNumberError = null;
                if (digitsOnly.length > 0 && digitsOnly.length !== 9) {
                    routingNumberError = [t('international_transfers_usd_routing_number_error')];
                }
                setErrors((prevState) => ({
                    ...prevState,
                    routing_number: routingNumberError,
                }));
                return digitsOnly;
            },
            [t],
        );

        const formatAndValidateAccountNumber = React.useCallback(
            (value) => {
                const digitsOnly = value.replace(/\D/g, '').slice(0, 17);
                let accountNumberError = null;
                if (value.length > 0 && /\D/.test(value)) {
                    accountNumberError = [t('international_transfers_usd_account_number_error')];
                }
                setErrors((prevState) => ({
                    ...prevState,
                    account_number: accountNumberError,
                }));
                return digitsOnly;
            },
            [t],
        );

        const hasRequiredFieldNotFilled = Object.values(formData).some((value) => !value)
            || formData.routing_number.length !== 9
            || !!errors.routing_number
            || !!errors.account_number;

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return formRef.current.click();
                },
                canClickOnPay: !hasRequiredFieldNotFilled,
            }),
            [hasRequiredFieldNotFilled],
        );

        return (
            <div id="wz-lyriapay__international-transfers-usd" className={styles.common_psp}>
                <form noValidate onSubmit={submitForm}>
                    <div className={styles.common_psp_form}>
                        <span className={internationalTransfersUsdStyles.credit_input_fields_block_descriptor}>
                            {t('international_transfers_usd_user_information')}
                        </span>
                        <div className={`${styles.common_field_group} ${internationalTransfersUsdStyles.bank_fields_wrapper}`}>
                            <FormField
                                name="first_name"
                                value={formData?.first_name}
                                onChange={onChangeFormField('first_name')}
                                label={t('international_transfers_usd_first_name')}
                                errors={errors.first_name}
                                required
                            />
                            <FormField
                                name="last_name"
                                value={formData?.last_name}
                                onChange={onChangeFormField('last_name')}
                                label={t('international_transfers_usd_last_name')}
                                errors={errors.last_name}
                                required
                            />
                            <FormField
                                name="country"
                                value={formData?.country}
                                onChange={onChangeFormField('country')}
                                label={t('international_transfers_usd_country')}
                                errors={errors.country}
                                required
                                type="select"
                                options={[
                                    { value: COUNTRY_UNITED_STATES, label: COUNTRY_UNITED_STATES },
                                ]}
                            />
                        </div>
                        <span>
                            {t('international_transfers_usd_bank_information')}
                        </span>
                        <div className={`${styles.common_field_group} ${internationalTransfersUsdStyles.bank_fields_wrapper}`}>
                            <FormField
                                name="bank_account_type"
                                value={formData?.bank_account_type}
                                onChange={onChangeFormField('bank_account_type')}
                                label={t('international_transfers_usd_bank_account_type')}
                                errors={errors.bank_account_type}
                                required
                                type="select"
                                options={ACCOUNT_TYPE_OPTIONS}
                            />
                            <FormField
                                name="routing_number"
                                value={formData?.routing_number}
                                onChange={onChangeFormField('routing_number')}
                                label={t('international_transfers_usd_routing_number')}
                                errors={errors.routing_number}
                                formatAndValidate={formatAndValidateRoutingNumber}
                                inputProps={{
                                    maxLength: '9',
                                    inputMode: 'numeric',
                                }}
                                required
                            />
                            <FormField
                                name="account_number"
                                value={formData?.account_number}
                                onChange={onChangeFormField('account_number')}
                                label={t('international_transfers_usd_account_number')}
                                errors={errors.account_number}
                                formatAndValidate={formatAndValidateAccountNumber}
                                inputProps={{
                                    maxLength: '17',
                                    inputMode: 'numeric',
                                }}
                                required
                            />
                        </div>
                    </div>
                    <div className={styles.common_psp_submit}>
                        <Button
                            disabled={hasRequiredFieldNotFilled}
                            label={labelPayButton || t('payButton')}
                            ref={formRef}
                            type='submit'
                            hideButton={hidePayButton}
                        />
                    </div>
                </form>
            </div>
        );
    },
);

InternationalTransfersUsd.propTypes = {
    /**
     * The is of the PSP returned by the backend in order to be able to pass it when we submit the form data.
     */
    pspId: PropTypes.number.isRequired,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
};

InternationalTransfersUsd.displayName = 'InternationalTransfersUsd';

export default InternationalTransfersUsd;
